import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from '../../axios';

var STATUS_LABELS = {
  active: '有効',
  stopped: '停止',
  reached: '上限到達',
  no_balance: '停止（残高なし）',
  unset: '未設定',
};

var PrPointSection = function (props) {
  var limit = props.limit;
  var consumed = props.consumed;
  var remaining = props.remaining;
  var efficiency = props.efficiency;
  var status = props.status;
  var eventId = props.eventId;
  var purchaseUrl = props.purchaseUrl;
  var detailUrl = props.detailUrl;
  var onDataChange = props.onDataChange;
  var circle_balance = props.circle_balance;
  var circle_next_expiry = props.circle_next_expiry;

  var isActive = status === 'active' || status === 'reached' || status === 'no_balance';
  var isStopped = status === 'stopped';
  var canEdit = isActive || isStopped || status === 'unset';

  var hasLimit = limit !== null && limit !== undefined;
  var progressPercent = hasLimit && limit > 0 ? Math.min((consumed / limit) * 100, 100) : 0;
  var progressLevel = progressPercent >= 85 ? 'danger' : progressPercent >= 60 ? 'warning' : 'normal';
  var usageRate = hasLimit && limit > 0 ? Math.round((consumed / limit) * 100) : null;

  var _useState = useState(limit !== null ? String(limit) : '');
  var editLimit = _useState[0];
  var setEditLimit = _useState[1];

  var _useState2 = useState(null);
  var processing = _useState2[0];
  var setProcessing = _useState2[1];

  var _useState3 = useState(null);
  var notification = _useState3[0];
  var setNotification = _useState3[1];

  var _useState4 = useState(false);
  var showDeactivateConfirm = _useState4[0];
  var setShowDeactivateConfirm = _useState4[1];

  useEffect(function () {
    if (notification) {
      var timer = setTimeout(function () { setNotification(null); }, 4000);
      return function () { clearTimeout(timer); };
    }
  }, [notification]);

  var showNotify = function (type, message) {
    setNotification({ type: type, message: message });
  };

  var handleUpdateLimit = async function () {
    setProcessing('update');
    try {
      var limitValue = editLimit === '' ? null : parseInt(editLimit, 10);
      await axios.post('/api/events/' + eventId + '/pr_limitations/update', {
        event_id: eventId,
        limit: limitValue,
      });
      showNotify('success', status === 'unset' ? 'PRポイント設定を開始しました' : '消費上限を更新しました');
      if (onDataChange) onDataChange();
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || '設定の更新に失敗しました';
      showNotify('error', msg);
    }
    setProcessing(null);
  };

  var handleDeactivate = async function () {
    setProcessing('toggle');
    setShowDeactivateConfirm(false);
    try {
      await axios.post('/api/events/' + eventId + '/pr_limitations/deactivate', {
        event_id: eventId,
      });
      showNotify('success', 'ポイント利用を停止しました');
      if (onDataChange) onDataChange();
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || '停止処理に失敗しました';
      showNotify('error', msg);
    }
    setProcessing(null);
  };

  var handleActivate = async function () {
    setProcessing('toggle');
    try {
      var limitValue = editLimit === '' ? null : parseInt(editLimit, 10);
      await axios.post('/api/events/' + eventId + '/pr_limitations/update', {
        event_id: eventId,
        limit: limitValue,
      });
      showNotify('success', 'ポイント利用を再開しました');
      if (onDataChange) onDataChange();
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || '再開処理に失敗しました';
      showNotify('error', msg);
    }
    setProcessing(null);
  };

  return (
    <div className="o-dashboard__section">
      <h3 className="o-dashboard__section-title">PRポイント</h3>
      <div className="o-pr-point">
        {notification && (
          <div
            className={'o-pr-point__notification o-pr-point__notification--' + notification.type}
            role="alert"
            aria-live="polite"
          >
            <i className={'fas ' + (notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle')} />
            <span>{notification.message}</span>
            <button
              type="button"
              className="o-pr-point__notification-close"
              onClick={function () { setNotification(null); }}
              aria-label="通知を閉じる"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        <div className="o-pr-point__header">
          <span
            className={'o-pr-point__status o-pr-point__status--' + status}
            role="status"
            aria-label={'PRポイントステータス: ' + (STATUS_LABELS[status] || status)}
          >
            {STATUS_LABELS[status] || status}
          </span>
          <div className="o-pr-point__header-links">
            {detailUrl && (
              <a href={detailUrl} className="o-pr-point__detail-link">
                有効期限・内訳 &rarr;
              </a>
            )}
            {purchaseUrl && (
              <a href={purchaseUrl} className="o-pr-point__purchase-link">
                PRチケット購入 &rarr;
              </a>
            )}
          </div>
        </div>

        {status === 'unset' && (
          <div className="o-pr-point__guidance">
            <i className="fas fa-info-circle" />
            <span>PRポイントを設定すると、イベントページが検索結果で優先表示されます。</span>
          </div>
        )}

        {status === 'no_balance' && (
          <div className="o-pr-point__guidance o-pr-point__guidance--warning">
            <span>
              サークルのPRポイント残高が0のため、PRポイントが消費されていません。
              優先表示を継続するには「PRチケット購入」から追加してください。
            </span>
          </div>
        )}

        {hasLimit && (
          <div className="o-pr-point__progress">
            <div className="o-pr-point__progress-header">
              <span className="o-pr-point__progress-label">イベント消費</span>
              <span className="o-pr-point__progress-value">
                <strong>{Number(consumed).toLocaleString()}</strong>
                {' / '}
                {Number(limit).toLocaleString()} pt
                {usageRate !== null && (
                  <span className={'o-pr-point__progress-percent o-pr-point__progress-percent--' + progressLevel}>
                    {' '}({usageRate}%)
                  </span>
                )}
              </span>
            </div>
            <div
              className="o-pr-point__progress-track"
              role="progressbar"
              aria-valuenow={Math.round(progressPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={'消費進捗: ' + Math.round(progressPercent) + '%'}
            >
              <div
                className={'o-pr-point__progress-fill o-pr-point__progress-fill--' + progressLevel}
                style={{ width: progressPercent + '%' }}
              />
            </div>
          </div>
        )}

        <div className="o-pr-point__metrics">
          <div className="o-pr-point__metric">
            <span className="o-pr-point__metric-label">上限の残り枠</span>
            <span className="o-pr-point__metric-value">{remaining !== null ? Number(remaining).toLocaleString() : '無制限'}</span>
            {remaining !== null && <span className="o-pr-point__metric-unit">pt</span>}
          </div>
          <div className="o-pr-point__metric">
            <span className="o-pr-point__metric-label">サークル残高</span>
            <span className="o-pr-point__metric-value">{Number(circle_balance || 0).toLocaleString()}</span>
            <span className="o-pr-point__metric-unit">pt</span>
            {circle_next_expiry && (
              <span className="o-pr-point__next-expiry">
                直近の期限 {circle_next_expiry.expired_on}（{Number(circle_next_expiry.points).toLocaleString()}pt）
              </span>
            )}
          </div>
          <div className="o-pr-point__metric">
            <span className="o-pr-point__metric-label">消費率</span>
            <span className="o-pr-point__metric-value">{usageRate !== null ? usageRate : '—'}</span>
            {usageRate !== null && <span className="o-pr-point__metric-unit">%</span>}
          </div>
          <div className="o-pr-point__metric o-pr-point__metric--accent">
            <span className="o-pr-point__metric-label">PR効率</span>
            <span className="o-pr-point__metric-value">{efficiency}</span>
            <span className="o-pr-point__metric-unit">PV/pt</span>
          </div>
        </div>

        {canEdit && (
          <div className="o-pr-point__control-section">
            <div className="o-pr-point__limit-row">
              <label className="o-pr-point__label" htmlFor={'pr-limit-' + eventId}>消費上限</label>
              <div className="o-pr-point__limit-group">
                <input
                  id={'pr-limit-' + eventId}
                  type="number"
                  className="o-pr-point__input"
                  value={editLimit}
                  placeholder="無制限"
                  min={consumed + 1}
                  disabled={!canEdit}
                  onChange={function (e) { setEditLimit(e.target.value); }}
                  aria-describedby={'pr-limit-helper-' + eventId}
                />
                <span className="o-pr-point__input-unit">pt</span>
              </div>
              <button
                type="button"
                className="o-pr-point__btn o-pr-point__btn--primary"
                onClick={handleUpdateLimit}
                disabled={processing === 'update'}
                aria-label={processing === 'update' ? '処理中' : (status === 'unset' ? '設定を保存して開始' : '上限を更新')}
              >
                {processing === 'update' ? (
                  <span><i className="fas fa-spinner fa-pulse" /> 処理中</span>
                ) : (
                  status === 'unset' ? '設定を保存 & 開始' : '上限更新'
                )}
              </button>
            </div>
            <p className="o-pr-point__helper" id={'pr-limit-helper-' + eventId}>
              空欄にすると無制限になります
            </p>

            <div className="o-pr-point__toggle-row">
              {isActive && !showDeactivateConfirm && (
                <button
                  type="button"
                  className="o-pr-point__btn o-pr-point__btn--danger-outline"
                  onClick={function () { setShowDeactivateConfirm(true); }}
                  disabled={processing === 'toggle'}
                >
                  ポイント利用を停止
                </button>
              )}
              {isActive && showDeactivateConfirm && (
                <div className="o-pr-point__confirm">
                  <span className="o-pr-point__confirm-text">
                    <i className="fas fa-exclamation-triangle" /> 本当にポイント利用を停止しますか？
                  </span>
                  <div className="o-pr-point__confirm-actions">
                    <button
                      type="button"
                      className="o-pr-point__btn o-pr-point__btn--danger"
                      onClick={handleDeactivate}
                      disabled={processing === 'toggle'}
                      aria-label={processing === 'toggle' ? '停止処理中' : 'ポイント利用を停止する'}
                    >
                      {processing === 'toggle' ? (
                        <span><i className="fas fa-spinner fa-pulse" /> 処理中</span>
                      ) : '停止する'}
                    </button>
                    <button
                      type="button"
                      className="o-pr-point__btn o-pr-point__btn--ghost"
                      onClick={function () { setShowDeactivateConfirm(false); }}
                      disabled={processing === 'toggle'}
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
              {isStopped && (
                <button
                  type="button"
                  className="o-pr-point__btn o-pr-point__btn--success"
                  onClick={handleActivate}
                  disabled={processing === 'toggle'}
                  aria-label={processing === 'toggle' ? '再開処理中' : 'ポイント利用を再開する'}
                >
                  {processing === 'toggle' ? (
                    <span><i className="fas fa-spinner fa-pulse" /> 処理中</span>
                  ) : 'ポイント利用を再開'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PrPointSection.propTypes = {
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  consumed: PropTypes.number.isRequired,
  remaining: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  efficiency: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  eventId: PropTypes.number.isRequired,
  purchaseUrl: PropTypes.string,
  detailUrl: PropTypes.string,
  onDataChange: PropTypes.func,
  circle_balance: PropTypes.number,
  circle_next_expiry: PropTypes.shape({
    expired_on: PropTypes.string,
    points: PropTypes.number,
  }),
};

export default PrPointSection;
